/**
 * Public contracts for the unified file-preview module.
 *
 * Two orthogonal abstractions:
 *   - `FileSource` decouples "where does the content live" (URL, DB artifact,
 *     inline string) from the dialog chrome and from the renderers.
 *   - `FileRenderer` decouples "how is this MIME / kind shown" (PDF iframe,
 *     image, markdown, csv table, …) from both sources and chrome.
 *
 * Renderers receive a `FileSource` and call only the accessor methods they
 * need (`getText` / `getBlobUrl` / `getBytes`). They never know whether the
 * bytes came from S3, a database row, or a literal string — so adding a new
 * source type doesn't ripple through every renderer, and vice versa.
 */
import type { ComponentType, ReactNode } from "react";

/**
 * Coarse classification of how a file should be rendered. Set explicitly on
 * the source when the caller already knows (artifacts have a `kind` column;
 * tool outputs declare it) — otherwise the registry infers it from
 * `contentType` and filename extension.
 */
export type FileKind =
  | "pdf"
  | "image"
  | "html"
  | "markdown"
  | "document"
  | "code"
  | "csv"
  | "text"
  | "spreadsheet"
  | "presentation"
  | "audio"
  | "video"
  | "docx"
  | "unsupported";

/**
 * Capability flags a source advertises. The chrome reads these to decide
 * which action buttons (download, open-external, …) to render. Renderers
 * may also consult them to pick a fallback path.
 */
export interface FileSourceCapabilities {
  /** Caller can produce a download URL (or trigger a download some other way). */
  canDownload: boolean;
  /** A direct URL exists that can be opened in a new tab. */
  canOpenExternal: boolean;
}

/**
 * The "where content lives" abstraction. Implementations: `urlSource`,
 * `artifactSource`, `inlineSource` (and any future addition).
 *
 * All accessor methods are optional — implementations only fulfil the ones
 * relevant to their backing store. Renderers MUST tolerate the absence of
 * accessors they would have liked (e.g. a markdown renderer falling back to
 * `getBytes()` when `getText()` isn't available).
 *
 * Methods are async + lazy so a renderer that never needs the bytes (e.g.
 * an image renderer that just hands the URL to `<img src>`) doesn't pay
 * for a fetch.
 */
export interface FileSource {
  /** Stable identity — used for React keys and refetch hints. */
  id: string;

  /** Display name, e.g. "Q3 Report.pdf". */
  filename: string;

  /**
   * MIME type when known. Renderer dispatch falls back to the filename
   * extension when this is missing or generic (`application/octet-stream`).
   */
  contentType?: string;

  /**
   * Pre-classified kind. When set, overrides the MIME / extension dispatch
   * in the registry — useful for artifacts (`kind: 'presentation'` etc.)
   * where the MIME alone can't carry the semantics.
   */
  kind?: FileKind;

  /** Byte count for footer display. Undefined when not pre-fetched. */
  size?: number;

  /** What actions chrome should expose for this source. */
  capabilities: FileSourceCapabilities;

  /**
   * Resolve the content as a UTF-8 string. Used by markdown / code / csv /
   * text renderers. Implementations may cache the result for the lifetime
   * of the source instance — renderers must NOT rely on idempotency
   * guarantees beyond that.
   */
  getText?: () => Promise<string>;

  /**
   * Resolve a URL the browser can render directly (iframe `src`, `<img>`
   * `src`, `<embed>`). When `inline: true`, the URL must produce a
   * response whose headers cause the browser to render in-place rather
   * than download — for cross-origin URLs that means
   * `Content-Disposition: inline` AND a correct `Content-Type`.
   *
   * For DB-backed sources this typically constructs a blob URL on the
   * fly via `URL.createObjectURL(new Blob([content], {type}))`; the
   * `inline` option is moot in that case.
   */
  getBlobUrl?: (opts?: { inline?: boolean }) => Promise<string>;

  /** Resolve raw bytes. Fallback for arbitrary binary types. */
  getBytes?: () => Promise<ArrayBuffer>;

  /** Resolve a URL suitable for `<a download>`. May 401 on cross-origin. */
  resolveDownloadUrl?: () => Promise<string>;

  /** Resolve a URL suitable for "open in new tab". */
  resolveExternalUrl?: () => Promise<string>;
}

/** Props every renderer receives. */
export interface FileRendererProps {
  source: FileSource;
}

/**
 * A single renderer entry in the registry. Renderers are matched in order
 * of descending `priority`, so more specific matchers (e.g. exact MIME)
 * should outrank generic ones (e.g. "any text-like file").
 *
 * The final entry in the registry should match unconditionally so the
 * dispatch never returns null — that fallback is the "unsupported"
 * renderer.
 */
export interface FileRenderer {
  /** Higher wins on ties. Defaults to 0 when omitted. */
  priority?: number;
  /** Predicate over the source — return true to select this renderer. */
  matches: (source: FileSource) => boolean;
  /** Renderer component. */
  Component: ComponentType<FileRendererProps>;
}

/**
 * Props for the dialog chrome. Pass `source: null` to keep the dialog
 * closed without unmounting the parent — useful when the source is
 * built lazily on open.
 *
 * `actions` is rendered into the header slot, to the left of the always-
 * present Download / Open-external buttons (which are themselves shown
 * only when the source's capabilities allow them).
 */
export interface FilePreviewDialogProps {
  source: FileSource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions?: ReactNode;
}

/**
 * An `Error` a source throws when the file itself is gone (HTTP 404), as opposed
 * to a transport/CORS failure. Renderers use {@link isFileNotFoundError} to tell
 * the two apart: a missing file should surface the friendly message, never fall
 * back to iframing a URL that only re-renders the server's error body.
 */
export interface FileNotFoundError extends Error {
  notFound?: boolean;
}

/** True when the error marks the file as genuinely gone (see {@link FileNotFoundError}). */
export function isFileNotFoundError(error: unknown): boolean {
  return error instanceof Error && (error as FileNotFoundError).notFound === true;
}
